import { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { Phone, Sparkles } from "lucide-react";
import { useTr } from "@/i18n";

const genres = ["Anni 80/90", "Disco Dance", "Afro House", "Techno"];

const maskReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const tr = useTr();
  const [videoSrc, setVideoSrc] = useState(null);
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 20 });
  const sy = useSpring(my, { stiffness: 55, damping: 20 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const onMove = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 22);
    my.set((e.clientY / window.innerHeight - 0.5) * 16);
  };

  // Il video di sfondo (3.3 MB) parte solo a pagina caricata e mai su connessioni lente/save-data
  useEffect(() => {
    const conn = navigator.connection || {};
    if (conn.saveData || (conn.effectiveType || "").includes("2g")) return;
    const start = () => setVideoSrc("/assets/serata.mp4");
    if (document.readyState === "complete") {
      const id = setTimeout(start, 1200);
      return () => clearTimeout(id);
    }
    const onLoad = () => setTimeout(start, 1200);
    window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <motion.video
        data-testid="hero-video"
        aria-hidden="true"
        tabIndex={-1}
        style={{ y: videoY }}
        className="absolute inset-0 w-full h-[115%] object-cover opacity-40"
        src={videoSrc || undefined}
        poster="/assets/hero-poster-768.webp"
        preload="none"
        autoPlay
        muted
        loop
        playsInline
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/55 to-[#080808]" />

      <motion.div
        style={{ x: sx, y: sy, opacity: fade }}
        className="relative z-10 text-center px-6 max-w-5xl mx-auto pt-24 pb-12 sm:pt-28 sm:pb-16"
      >
        <motion.img
          src="/assets/logo-hero-256.webp"
          alt="DJ Magix - Tutta Musica"
          data-testid="hero-logo"
          width={800}
          height={800}
          fetchPriority="high"
          className="h-24 w-24 sm:h-40 sm:w-40 mx-auto mb-5 sm:mb-8 object-contain drop-shadow-[0_0_35px_rgba(226,184,87,0.35)]"
          initial={{ opacity: 0, scale: 0.8, rotate: -6 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
        >
          <span
            data-testid="hero-experience-badge"
            className="inline-flex items-center gap-2 rounded-full gold-frame bg-black/50 px-5 py-2 text-xs uppercase tracking-[0.3em] text-[#F7DE8B]"
          >
            <Sparkles size={14} /> {tr("hero.badge", "25 Anni di Esperienza")}
          </span>
        </motion.div>

        <h1 data-testid="hero-title" className="font-display font-bold tracking-tight mt-5 sm:mt-8 leading-[0.92]">
          <span className="sr-only">{tr("hero.srTitle", "DJ set per matrimoni, feste ed eventi in Svizzera — ")}</span>
          <span className="block overflow-hidden pb-1">
            <motion.span
              variants={maskReveal}
              custom={0}
              initial="hidden"
              animate="show"
              className="block gold-text text-5xl sm:text-7xl lg:text-8xl"
            >
              DJ
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              variants={maskReveal}
              custom={1}
              initial="hidden"
              animate="show"
              className="block gold-text text-5xl sm:text-7xl lg:text-8xl"
            >
              MAGIX
            </motion.span>
          </span>
        </h1>

        <span className="block overflow-hidden mt-2">
          <motion.span
            variants={maskReveal}
            custom={2}
            initial="hidden"
            animate="show"
            className="block font-accent italic text-xl sm:text-2xl text-[#E2C882]/90"
          >
            — Tutta Musica —
          </motion.span>
        </span>

        <motion.p
          className="text-xs sm:text-sm uppercase tracking-[0.35em] text-[#BFA45D] mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.95 }}
        >
          {tr("hero.tagline", "Musica • Emozione • Tecnologia")}
        </motion.p>

        <motion.p
          className="text-base sm:text-lg text-[#E2C882]/85 mt-4 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 1.05 }}
        >
          {tr("hero.subtitle", "DJ set di alto profilo in tutta la Svizzera: compleanni, feste di paese, matrimoni, club ed eventi aziendali. Producer e sound su misura.")}
        </motion.p>

        <motion.div
          className="flex flex-wrap justify-center gap-2.5 mt-5 sm:mt-7"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 1.15 }}
        >
          {genres.map((g) => (
            <span
              key={g}
              className="rounded-full border border-[rgba(226,184,87,0.35)] bg-black/40 px-4 py-1.5 text-xs tracking-[0.15em] uppercase text-[#E2C882]"
            >
              {g}
            </span>
          ))}
        </motion.div>

        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-6 sm:mt-10"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.25 }}
        >
          <a
            href="#contatti"
            data-testid="hero-cta-quote"
            className="gold-btn rounded-full w-full sm:w-auto px-8 py-4 sm:py-3.5 text-sm font-bold uppercase tracking-[0.15em] text-center"
          >
            {tr("hero.ctaQuote", "Richiedi Preventivo Gratuito")}
          </a>
          <a
            href="tel:+41764411359"
            data-testid="hero-cta-call"
            className="inline-flex items-center justify-center gap-2 rounded-full gold-frame bg-black/40 w-full sm:w-auto px-8 py-4 sm:py-3.5 text-sm uppercase tracking-[0.15em] text-[#F7DE8B] hover:bg-[#E2B857]/10 transition-colors"
          >
            <Phone size={16} /> {tr("hero.ctaCall", "Chiama Ora")}
          </a>
        </motion.div>

        <motion.div
          className="flex items-end justify-center gap-1.5 h-9 sm:h-14 mt-7 sm:mt-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          aria-hidden="true"
        >
          {[...Array(28)].map((_, i) => (
            <span
              key={i}
              className="eq-bar w-1.5 h-full rounded-sm bg-gradient-to-t from-[#9E7B28] to-[#F7DE8B]"
              style={{ animationDelay: `${i * 0.07}s`, animationDuration: `${0.9 + (i % 5) * 0.12}s` }}
            />
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#9F8A52]">{tr("hero.scroll", "Scorri")}</span>
        <span className="flex h-9 w-5 items-start justify-center rounded-full border border-[rgba(226,184,87,0.4)] p-1.5">
          <span className="scroll-dot h-1.5 w-1.5 rounded-full bg-[#E2B857]" />
        </span>
      </motion.div>
    </section>
  );
}
